import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, Users, Check, Eye, Brain, Shield, Loader2, Link2 } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import type { Persona, Product } from "@shared/schema";
import { getQueryFn } from "@/lib/queryClient";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { optimizedImg } from "@/lib/img";

interface Narrative {
  id: string;
  name: string;
  compatiblePersonasJson: string[] | null;
  compatibleProductsJson: string[] | null;
}

interface PersonaStepProps {
  brandId: string;
  selectedPersona: Persona | null;
  selectedProduct: Product | null;
  onSelect: (persona: Persona) => void;
}

export function PersonaStep({ brandId, selectedPersona, selectedProduct, onSelect }: PersonaStepProps) {
  const [search, setSearch] = useState("");

  const { data: personas = [], isLoading: loadingPersonas } = useQuery<Persona[]>({
    queryKey: [`/api/brands/${brandId}/personas`],
    queryFn: getQueryFn({ on401: "returnNull" }) as () => Promise<Persona[]>,
    enabled: !!brandId,
  });

  const { data: narratives = [] } = useQuery<Narrative[]>({
    queryKey: [`/api/brands/${brandId}/narratives`],
    queryFn: getQueryFn({ on401: "returnNull" }) as () => Promise<Narrative[]>,
    enabled: !!brandId,
  });

  const personaNarrativeMap = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const n of narratives) {
      const compatPersonas = Array.isArray(n.compatiblePersonasJson) ? n.compatiblePersonasJson : [];
      const compatProducts = Array.isArray(n.compatibleProductsJson) ? n.compatibleProductsJson : [];
      const matchesProduct = !selectedProduct || compatProducts.includes(selectedProduct.id);
      if (matchesProduct) {
        for (const pId of compatPersonas) {
          const existing = map.get(pId) || [];
          existing.push(n.name);
          map.set(pId, existing);
        }
      }
    }
    return map;
  }, [narratives, selectedProduct]);

  const { connected, other } = useMemo(() => {
    const activePersonas = personas.filter((p) => p.status !== "archived");

    let list = activePersonas;
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.summary?.toLowerCase().includes(q) ||
          p.awarenessStage?.toLowerCase().includes(q)
      );
    }

    const conn: Persona[] = [];
    const rest: Persona[] = [];
    for (const p of list) {
      if (personaNarrativeMap.has(p.id)) {
        conn.push(p);
      } else {
        rest.push(p);
      }
    }
    return { connected: conn, other: rest };
  }, [search, personas, personaNarrativeMap]);

  const awarenessLabel = (stage: string | null | undefined) => {
    if (!stage) return "";
    return stage.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
  };

  const renderPersonaCard = (persona: Persona, narrativeNames?: string[]) => {
    const isSelected = selectedPersona?.id === persona.id;
    return (
      <Card
        key={persona.id}
        className={`p-4 cursor-pointer transition-all ${
          isSelected
            ? "border-primary/40 bg-primary/[0.03] ring-1 ring-primary/20"
            : "hover-elevate"
        }`}
        onClick={() => onSelect(persona)}
        data-testid={`card-persona-${persona.id}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 overflow-hidden">
              {persona.imageUrl ? (
                <img src={optimizedImg(persona.imageUrl, 72)} alt={persona.name} className="w-full h-full object-cover" loading="lazy" />
              ) : (
                <Users className="h-4 w-4 text-primary" />
              )}
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold" data-testid={`text-persona-name-${persona.id}`}>
                  {persona.name}
                </h3>
                {persona.awarenessStage && (
                  <Badge variant="secondary" className="text-[10px]">
                    {awarenessLabel(persona.awarenessStage)}
                  </Badge>
                )}
                {persona.status === "draft" && (
                  <Badge variant="outline" className="text-[10px]">
                    Draft
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {persona.summary}
              </p>
              {narrativeNames && narrativeNames.length > 0 && (
                <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                  <Link2 className="h-3 w-3 text-primary/60 shrink-0" />
                  {narrativeNames.map((name) => (
                    <Badge key={name} variant="outline" className="text-[10px] border-primary/20 text-primary/80">
                      {name}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          {isSelected && (
            <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
              <Check className="h-3 w-3" />
            </div>
          )}
        </div>
      </Card>
    );
  };

  return (
    <div className="space-y-6" data-testid="step-persona">
      <div>
        <h2 className="text-xl font-bold tracking-tight" data-testid="text-step-title">
          Select Persona
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Choose the target persona for this campaign
        </p>
      </div>

      <div className="space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="search"
            placeholder="Search personas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="input-persona-search"
          />
        </div>

        {loadingPersonas ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            <span className="ml-2 text-sm text-muted-foreground">Loading personas...</span>
          </div>
        ) : connected.length === 0 && other.length === 0 ? (
          <div className="text-center py-12">
            <Users className="h-8 w-8 text-muted-foreground/30 mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">
              {search ? "No personas match your search" : "No personas found for this brand"}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {connected.length > 0 && (
              <div className="space-y-2">
                {selectedProduct && (
                  <p className="text-[10px] font-semibold text-primary uppercase tracking-wider">
                    Connected to {selectedProduct.name}
                  </p>
                )}
                <div className="grid gap-3">
                  {connected.map((persona) =>
                    renderPersonaCard(persona, personaNarrativeMap.get(persona.id))
                  )}
                </div>
              </div>
            )}

            {other.length > 0 && (
              <div className="space-y-2">
                {connected.length > 0 && (
                  <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider">
                    Other Personas
                  </p>
                )}
                <div className="grid gap-3">
                  {other.map((persona) => renderPersonaCard(persona))}
                </div>
              </div>
            )}
          </div>
        )}

        {selectedPersona && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <Card className="p-4 border-primary/20 bg-primary/[0.02]" data-testid="card-selected-persona-summary">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                Persona Profile
              </h4>
              <div className="space-y-2">
                <div className="flex items-start gap-2">
                  <Brain className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <span className="text-[10px] text-muted-foreground">Worldview</span>
                    <p className="text-xs">{selectedPersona.worldview || "Not specified"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Eye className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <span className="text-[10px] text-muted-foreground">Primary Pain</span>
                    <p className="text-xs">{selectedPersona.primaryPain || "Not specified"}</p>
                  </div>
                </div>
                <div className="flex items-start gap-2">
                  <Shield className="h-3.5 w-3.5 text-muted-foreground mt-0.5 shrink-0" />
                  <div>
                    <span className="text-[10px] text-muted-foreground">Deep Fear</span>
                    <p className="text-xs">{selectedPersona.deepFear || "Not specified"}</p>
                  </div>
                </div>
              </div>
            </Card>
          </motion.div>
        )}
      </div>
    </div>
  );
}
